import { mediaDialogUtils } from '../utils';

import type {
  AppData,
  DocumentServicesObject,
} from '@wix/document-services-types';
import type { PlatformApiMethods } from '@wix/editor-platform-host-integration-apis';
import type { TPAMessage, TPAMessageCallback } from '../types/tpaMessage';

export const createTpaMediaDialogService = (
  platformApiMethods: PlatformApiMethods,
  documentServicesAPI: DocumentServicesObject,
) => {
  const getAppDataFromMessage = (msg: TPAMessage): AppData | null => {
    if (!msg.compId) {
      return null;
    }

    try {
      const compRef = documentServicesAPI.components.get.byId(msg.compId);

      if (!compRef) {
        return null;
      }

      const componentData = documentServicesAPI.components.data.get(compRef);

      if (!componentData) {
        return null;
      }

      const appDefinitionId =
        componentData.appDefinitionId || componentData.applicationId;

      if (!appDefinitionId) {
        return null;
      }

      const appData =
        documentServicesAPI.platform.getAppDataByAppDefId(appDefinitionId);

      return appData || null;
    } catch (error) {
      console.warn('Failed to extract app data from message:', error);
      return null;
    }
  };

  const buildMediaPanelOptions = (msg: TPAMessage) => {
    if (!msg.data) {
      throw new Error('Message data is required to open media panel');
    }

    const platformCategory = mediaDialogUtils.getCategory(msg.data.mediaType);

    return {
      rawData: true,
      mediaType: platformCategory,
      isMultiSelect: msg.data.multiSelection ?? msg.data.isMultiSelect ?? true,
      ...(msg.data.imageAnalysis && { imageAnalysis: msg.data.imageAnalysis }),
    };
  };

  const openMediaPanel = async (
    msg: TPAMessage,
    callback: TPAMessageCallback,
    isSuperAppsFlow: boolean,
  ) => {
    const callOnCancel = msg.data?.callOnCancel ?? true;

    try {
      const options = buildMediaPanelOptions(msg);
      const appData = getAppDataFromMessage(msg) || ({} as AppData);
      const isMultiSelect = options.isMultiSelect;

      const result = await platformApiMethods.editor.openMediaPanel(
        appData,
        '',
        options,
      );

      if (!result && callOnCancel) {
        return callback({ wasCancelled: true });
      }

      if (!result || result.length === 0) {
        return callback(isMultiSelect ? [] : null);
      }

      const converter = isSuperAppsFlow
        ? mediaDialogUtils.convertMediaManagerResultForSuperApp
        : mediaDialogUtils.convertMediaManagerResult;

      const convertedResults = result.map(converter);

      if (!isMultiSelect && convertedResults.length) {
        return callback(convertedResults[0]);
      }

      return callback(convertedResults);
    } catch (error) {
      console.error('Error opening media panel:', error);

      if (callOnCancel) {
        return callback({
          wasCancelled: true,
          error: error instanceof Error ? error.message : 'Unknown error',
        });
      }

      throw error;
    }
  };

  return {
    openMediaPanel,
  };
};

export interface TpaMediaDialogService {
  openMediaPanel: (
    msg: TPAMessage,
    callback: TPAMessageCallback,
    isSuperAppsFlow: boolean,
  ) => Promise<void>;
}
